
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import employeeService from "../../services/employeeService";
import leaveService from "../../services/leaveService";
import { getErrorMessage } from "../../utils/errorHandler";
import Navbar from "../../components/layout/Navbar";
import Sidebar from "../../components/layout/Sidebar";
import PageHeader from "../../components/ui/PageHeader";
import StatCard from "../../components/ui/StatCard";
import LeaveRequestTable from "../../components/common/LeaveRequestTable";
import CalendarPreview from "../../components/common/CalendarPreview";
import {
  ClipboardList,
  Users,
  UserCheck,
  UserX,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../styles/dashboard.css";

const ManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allTeamRequests, setAllTeamRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Détection de la taille de l'écran
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Chargement des données du manager
  const loadManagerData = async () => {
    try {
      const [members, pending, allRequests] = await Promise.all([
        employeeService.getTeam(),
        leaveService.getLeaveRequestsManager("PENDING_MANAGER"),
        leaveService.getLeaveRequestsManager(),
      ]);

      setTeamMembers(Array.isArray(members) ? members : []);
      setPendingRequests(Array.isArray(pending) ? pending : []);
      setAllTeamRequests(Array.isArray(allRequests) ? allRequests : []);
    } catch (err) {
      console.error("Erreur lors du chargement des données :", err);

      setTeamMembers([]);
      setPendingRequests([]);
      setAllTeamRequests([]);

      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadManagerData();

  }, []);

  // Déterminer si un employé est en congé aujourd'hui
  const getEmployeeStatus = (employeeId) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activeLeave = allTeamRequests.find((req) => {
      if (
        Number(req.employee_id) !== Number(employeeId) ||
        req.status !== "APPROVED"
      ) {
        return false;
      }

      const start = new Date(req.start_date);
      const end = new Date(req.end_date);

      if (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime())
      ) {
        return false;
      }

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      return today >= start && today <= end;
    });

    return activeLeave ? "En congé" : "Présent";
  };

  const absentTodayCount = teamMembers.filter(
    (employee) => getEmployeeStatus(employee.id) === "En congé"
  ).length;

  const presentTodayCount = teamMembers.length - absentTodayCount;

  // Décision du manager sur une demande
  const handleAction = async (requestId, approved, comment = "") => {
    try {
      await leaveService.managerDecision(
        requestId,
        approved,
        comment
      );

const request = pendingRequests.find(
  (item) => Number(item.id) === Number(requestId)
);

const requesterRole = String(
  request?.employee_role ??
  request?.role ??
  request?.employee?.role ??
  ""
).toUpperCase();

const actionName = !approved
  ? "rejetée"
  : requesterRole === "RH"
  ? "validée définitivement par le manager"
  : "validée et transmise aux RH";

toast.success(`La demande a été ${actionName}.`);

      await loadManagerData();
    } catch (err) {
      console.error("Erreur lors de la décision :", err);
      toast.error(getErrorMessage(err));
    }
  };

  // Styles inline adaptatifs
  const pageStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: "100vh",
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  const contentWrapperStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  const mainStyle = {
    flex: "1 1 0%",
    width: isMobile ? "100%" : "auto",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    padding: isMobile ? "12px" : "24px",
    overflowX: "hidden",
  };

  const sectionStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    marginBottom: isMobile ? "18px" : "28px",
    boxSizing: "border-box",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    padding: isMobile ? "12px" : "20px",
    boxSizing: "border-box",
    borderRadius: "var(--radius-md)",
  };

  const titleStyle = {
    fontSize: isMobile ? "1rem" : "1.1rem",
    lineHeight: 1.4,
    fontWeight: 700,
    color: "var(--text-main)",
    margin: "0 0 8px",
    overflowWrap: "anywhere",
  };

  const descriptionStyle = {
    fontSize: isMobile ? "0.82rem" : "0.85rem",
    lineHeight: 1.6,
    color: "var(--text-muted)",
    margin: 0,
    overflowWrap: "anywhere",
  };

  // Écran de chargement
  if (loading) {
    return (
      <div
        className="dashboard-layout-container"
        style={pageStyle}
      >
        <Navbar />

        <div
          className="dashboard-content-wrapper"
          style={contentWrapperStyle}
        >
          {!isMobile && <Sidebar />}

          <main
            className="dashboard-main-content"
            style={{
              ...mainStyle,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "320px",
                padding: "16px",
                boxSizing: "border-box",
                textAlign: "center",
              }}
            >
              <div
                className="calendar-loading-spinner"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid var(--border)",
                  borderTopColor: "var(--primary)",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                }}
              />

              <p
                style={{
                  color: "var(--text-muted)",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                }}
              >
                Chargement des données de l'équipe...
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div
      className="dashboard-layout-container"
      style={pageStyle}
    >
      <Navbar />

      <div
        className="dashboard-content-wrapper"
        style={contentWrapperStyle}
      >
        {/* Sidebar affichée uniquement sur ordinateur */}
        {!isMobile && <Sidebar />}

        <main
          className="dashboard-main-content"
          style={mainStyle}
        >
          {/* En-tête */}
          <section style={sectionStyle}>
            <PageHeader
              title={`Espace Manager${user?.name ? ` — ${user.name}` : ""}`}
              subtitle="Suivi de votre équipe, validation initiale des demandes et plannings d'absences."
              actions={
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate("/manager/leaves")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    width: isMobile ? "100%" : "auto",
                    maxWidth: "100%",
                    minWidth: 0,
                    padding: "10px 14px",
                    boxSizing: "border-box",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    cursor: "pointer",
                  }}
                >
                  <ClipboardList
                    size={18}
                    style={{ flexShrink: 0 }}
                  />
                  <span>
                    Demandes à valider ({pendingRequests.length})
                  </span>
                </button>
              }
            />
          </section>

          {/* Statistiques */}
          <section style={sectionStyle}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "minmax(0, 1fr)"
                  : "repeat(auto-fit, minmax(220px, 1fr))",
                gap: isMobile ? "12px" : "16px",
                width: "100%",
                minWidth: 0,
              }}
            >
              <StatCard
                title="Membres de l'Équipe"
                value={teamMembers.length}
                subtitle={`${presentTodayCount} présents aujourd'hui`}
                icon={Users}
                iconBg="var(--primary-light)"
                iconColor="var(--primary)"
              />

              <StatCard
                title="À Valider"
                value={pendingRequests.length}
                subtitle="Demandes de congés en attente"
                icon={Clock}
                iconBg="#fef3c7"
                iconColor="#d97706"
                onClick={() => navigate("/manager/leaves")}
              />

              <StatCard
                title="Présents Aujourd'hui"
                value={presentTodayCount}
                subtitle="Collaborateurs à leur poste"
                icon={UserCheck}
                iconBg="var(--secondary-light)"
                iconColor="var(--secondary)"
              />

              <StatCard
                title="Absents Aujourd'hui"
                value={absentTodayCount}
                subtitle="En congé validé"
                icon={UserX}
                iconBg="var(--status-rejected-bg)"
                iconColor="var(--status-rejected-text)"
              />
            </div>
          </section>

          {/* Demandes de congés à approuver */}
          <section style={sectionStyle}>
            <div className="ui-card" style={cardStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "stretch" : "flex-start",
                  gap: "12px",
                  marginBottom: "16px",
                  minWidth: 0,
                }}
              >
                <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                  <h3 style={titleStyle}>
                    Demandes de congés à approuver
                  </h3>

                  <p style={descriptionStyle}>
                    Validez ou refusez les demandes soumises par les
                    membres de votre équipe.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate("/manager/leaves")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    width: isMobile ? "100%" : "auto",
                    maxWidth: "100%",
                    minWidth: 0,
                    padding: "9px 12px",
                    boxSizing: "border-box",
                    whiteSpace: "normal",
                    cursor: "pointer",
                  }}
                >
                  <ClipboardList
                    size={17}
                    style={{ flexShrink: 0 }}
                  />
                  Voir les demandes
                </button>
              </div>

              {pendingRequests.length === 0 ? (
                <p
                  style={{
                    textAlign: "center",
                    padding: "16px 8px",
                    color: "var(--text-muted)",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                  }}
                >
                  Aucune demande en attente de validation.
                </p>
              ) : (
                <div
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <LeaveRequestTable
                    requests={pendingRequests}
                    role="manager"
                    onAction={(id, action) =>
                      handleAction(
                        id,
                        action === "Approuver",
                        action === "Approuver"
                          ? "Visé par le manager"
                          : "Refusé par le manager"
                      )
                    }
                  />
                </div>
              )}
            </div>
          </section>

          {/* Présence de l'équipe */}
          <section style={sectionStyle}>
            <div className="ui-card" style={cardStyle}>
              <h3 style={titleStyle}>
                Statut de présence de l'équipe
              </h3>

              <p
                style={{
                  ...descriptionStyle,
                  marginBottom: "16px",
                }}
              >
                Consultez la présence des collaborateurs aujourd'hui.
              </p>

              {teamMembers.length === 0 ? (
                <p
                  style={{
                    textAlign: "center",
                    padding: "20px 8px",
                    color: "var(--text-muted)",
                  }}
                >
                  Aucun membre dans votre équipe.
                </p>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "minmax(0, 1fr)"
                      : "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: isMobile ? "10px" : "16px",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {teamMembers.map((member) => {
                    const status = getEmployeeStatus(member.id);
                    const isPresent = status === "Présent";

                    return (
                      <div
                        key={member.id}
                        style={{
                          padding: isMobile ? "12px" : "16px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--border)",
                          backgroundColor: "var(--bg-main)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                          minWidth: 0,
                          boxSizing: "border-box",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            flex: "1 1 0%",
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              minWidth: "36px",
                              borderRadius: "50%",
                              backgroundColor: "var(--primary-light)",
                              color: "var(--primary)",
                              fontWeight: 700,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            {member.first_name
                              ? member.first_name.charAt(0).toUpperCase()
                              : "E"}
                          </div>

                          <div
                            style={{
                              minWidth: 0,
                              overflowWrap: "anywhere",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                color: "var(--text-main)",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {member.first_name} {member.last_name}
                            </div>

                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "var(--text-muted)",
                                marginTop: "3px",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {member.job_title || "Équipe"}
                            </div>
                          </div>
                        </div>

                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "var(--radius-pill)",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            backgroundColor: isPresent
                              ? "var(--status-approved-bg)"
                              : "#fee2e2",
                            color: isPresent
                              ? "var(--status-approved-text)"
                              : "#dc2626",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Calendrier de l'équipe */}
          <section style={sectionStyle}>
            <div className="ui-card" style={cardStyle}>
              <h3 style={titleStyle}>
                Planning d'absence de l'équipe
              </h3>

              <p
                style={{
                  ...descriptionStyle,
                  marginBottom: "16px",
                }}
              >
                Consultez les périodes d'absence des membres de
                votre équipe.
              </p>

              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <CalendarPreview
                  requests={allTeamRequests.map((req) => ({
                    id: req.id,
                    employeeName: req.employee_name,
                    startDate: req.start_date,
                    endDate: req.end_date,
                    type: req.leave_type_name,
                    status:
                      req.status === "APPROVED"
                        ? "Approuvé"
                        : req.status === "REJECTED"
                        ? "Refusé"
                        : "En attente",
                  }))}
                  showEmployeeName={true}
                />
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ManagerDashboard;
